import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { VetShell } from '../components/VetShell';
import { type VetPetRecord, vetApi } from '../vet_api';

export function VetPetDetail() {
  const { petId } = useParams<{ petId: string }>();
  const [record, setRecord] = useState<VetPetRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!petId) return;
    vetApi<VetPetRecord>(`/api/vet/pets/${encodeURIComponent(petId)}`)
      .then(setRecord)
      .catch((err) => setError(err.message ?? 'Could not load this pet.'));
  }, [petId]);

  async function handleSendNote(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!petId || !noteBody.trim()) return;
    setSending(true);
    try {
      await vetApi(`/api/vet/pets/${encodeURIComponent(petId)}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body: noteBody.trim() }),
      });
      setSent(true);
      setNoteBody('');
    } catch (err) {
      const e = err as Error;
      setError(e.message ?? 'Could not send the note.');
    } finally {
      setSending(false);
    }
  }

  if (error) {
    return (
      <VetShell>
        <p className="text-sm text-rose-600">{error}</p>
        <Link to="/" className="mt-4 inline-block text-sm text-[#004AAD] hover:underline">
          ← Back to pets
        </Link>
      </VetShell>
    );
  }
  if (!record) {
    return (
      <VetShell>
        <p className="text-zinc-500">Loading…</p>
      </VetShell>
    );
  }

  const allergies = parseJsonArray(record.pet.allergiesJson);
  const medications = parseJsonArray(record.pet.medicationsJson);

  return (
    <VetShell>
      <Link to="/" className="text-sm text-[#004AAD] hover:underline">
        ← All pets
      </Link>
      <div className="mt-4 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{record.pet.name}</h1>
          <p className="mt-1 text-sm text-zinc-600">
            {record.pet.species}
            {record.pet.breed ? ` · ${record.pet.breed}` : ''}
            {record.pet.sex ? ` · ${record.pet.sex}` : ''}
            {record.pet.dateOfBirth ? ` · DOB ${record.pet.dateOfBirth}` : ''}
            {record.pet.weightKg ? ` · ${record.pet.weightKg} kg` : ''}
          </p>
        </div>
        <div className="text-right text-xs text-zinc-500">
          <p>Records held by</p>
          <p className="text-sm font-medium text-zinc-700">{record.kennel.name}</p>
        </div>
      </div>

      {record.pet.microchip ? (
        <p className="mt-3 text-xs text-zinc-500">
          Microchip {record.pet.microchip}
          {record.pet.microchipRegisteredWith ? ` · ${record.pet.microchipRegisteredWith}` : ''}
        </p>
      ) : null}

      <Section title="Vaccinations">
        {record.vaccinations.length === 0 ? (
          <p className="text-sm text-zinc-500">No vaccinations on file.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] divide-y divide-zinc-200 text-sm">
              <thead className="text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="py-2 pr-4">Vaccine</th>
                  <th className="py-2 pr-4">Administered</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2">Verified</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {record.vaccinations.map((v) => (
                  <tr key={v.id}>
                    <td className="py-2 pr-4 font-medium text-zinc-900">
                      {v.vaccineName ?? v.vaccineSlug ?? '—'}
                    </td>
                    <td className="py-2 pr-4 text-zinc-700">{v.administeredOn ?? '—'}</td>
                    <td className="py-2 pr-4 text-zinc-700">{v.expiresOn ?? '—'}</td>
                    <td className="py-2 text-zinc-500">
                      {v.verifiedAt ? (
                        <Check className="size-3.5" strokeWidth={1.75} aria-label="Verified" />
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {allergies.length > 0 ? (
        <Section title="Allergies">
          <ul className="list-disc pl-5 text-sm text-zinc-700">
            {allergies.map((a, idx) => (
              <li key={`allergy-${idx}`}>{a}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      {medications.length > 0 ? (
        <Section title="Medications">
          <ul className="list-disc pl-5 text-sm text-zinc-700">
            {medications.map((m, idx) => (
              <li key={`med-${idx}`}>{m}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      {record.pet.medicalConditions ? (
        <Section title="Medical conditions">
          <p className="whitespace-pre-line text-sm text-zinc-700">
            {record.pet.medicalConditions}
          </p>
        </Section>
      ) : null}

      {record.pet.behaviorNotes ? (
        <Section title="Behaviour notes">
          <p className="whitespace-pre-line text-sm text-zinc-700">{record.pet.behaviorNotes}</p>
        </Section>
      ) : null}

      {record.pet.specialInstructions ? (
        <Section title="Special instructions">
          <p className="whitespace-pre-line text-sm text-zinc-700">
            {record.pet.specialInstructions}
          </p>
        </Section>
      ) : null}

      <Section title="Recent incidents">
        {record.incidents.length === 0 ? (
          <p className="text-sm text-zinc-500">No incidents recorded.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {record.incidents.map((i) => (
              <li key={i.id} className="py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-zinc-900">
                    {i.incidentType}{' '}
                    <span className="font-normal text-zinc-500">· {i.severity}</span>
                  </p>
                  <p className="text-xs text-zinc-500">{i.occurredAt}</p>
                </div>
                <p className="mt-1 text-sm text-zinc-700">{i.description}</p>
                {i.actionTaken ? (
                  <p className="mt-1 text-xs text-zinc-500">Action: {i.actionTaken}</p>
                ) : null}
                {i.followUpRequired ? (
                  <p className="mt-1 text-xs text-amber-700">Follow-up required</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {record.pet.insuranceProvider ? (
        <Section title="Insurance">
          <p className="text-sm text-zinc-700">
            {record.pet.insuranceProvider}
            {record.pet.insurancePolicyNumber ? ` · ${record.pet.insurancePolicyNumber}` : ''}
            {record.pet.insuranceExpiresOn ? ` · expires ${record.pet.insuranceExpiresOn}` : ''}
          </p>
        </Section>
      ) : null}

      <Section title="Send a note to the kennel">
        <form onSubmit={handleSendNote} className="space-y-3">
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            rows={4}
            maxLength={4000}
            className="block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-[#004AAD] focus:outline-none focus:ring-1 focus:ring-[#004AAD]"
            placeholder={`Send a note about ${record.pet.name} to ${record.kennel.name}`}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-zinc-500">
              Staff at {record.kennel.name} will see this in their inbox.
            </p>
            <button
              type="submit"
              disabled={sending || noteBody.trim().length === 0}
              className="rounded-md bg-[#004AAD] px-4 py-2 text-sm font-semibold text-white hover:bg-[#003a8c] disabled:opacity-60"
            >
              {sending ? 'Sending…' : 'Send note'}
            </button>
          </div>
          {sent ? <p className="text-sm text-emerald-700">Note sent.</p> : null}
        </form>
      </Section>
    </VetShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 rounded-lg border border-zinc-200 bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function parseJsonArray(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    /* ignore */
  }
  return [];
}
